/** "Generate ePCR": closes the mission out and downloads the mission's ePCR
 *  (the patient care record with the device report; sentio
 *  missionagent/integration/closeout).
 *
 *  Not the handoff draft panel (the IMIST-AMBO handover the agents keep
 *  drafting): this is the one-off end of the mission. The first click, once
 *  confirmed, marks the mission complete and has the Mission Summariser report
 *  on the device's export; the download is that report as the summariser made
 *  it, only once it is ready (it takes a few minutes); a failed one can be
 *  retried.
 *
 *  Missions not from a Sentio device (external feeds) cannot be closed out:
 *  for those this stays the plain care record download. */

import { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material";
import { useCookies } from "react-cookie";

import { downloadCareRecord, errorMessage, fetchCloseout, startCloseout } from "../api";
import { ramp } from "../theme";
import type { Closeout } from "../types";
import { TOP_CONTROL_HEIGHT } from "./MissionPicker";

/** How often the close-out is re-read while the device report is prepared. */
const POLL_MS = 5000;

type MissionCloseoutButtonProps = {
  /** null while no engine-processed mission is selected. */
  missionId: string | null;
  /** The intervention id, for the dialog and the file name. */
  incident: string;
  /** Changed by the page when the close-out was started elsewhere (End mission): re-read it. */
  refreshKey?: number;
};

const inProgress = (closeout: Closeout | null) =>
  closeout !== null && closeout.stage !== "ready" && closeout.stage !== "failed";

const hhmm = (iso: string) => new Date(iso).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

export function MissionCloseoutButton(props: MissionCloseoutButtonProps) {
  const [cookies] = useCookies(["access_token"]);
  const token: string | undefined = cookies.access_token;
  const { missionId, incident, refreshKey } = props;
  const closable = missionId !== null && missionId.startsWith("sentio:");

  const [closeout, setCloseout] = useState<Closeout | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // The selected mission's close-out.
  useEffect(() => {
    setCloseout(null);
    setLoaded(false);
    setError(null);
    if (!token || !missionId || !closable) return;
    let cancelled = false;
    fetchCloseout(token, missionId)
      .then((body) => !cancelled && setCloseout(body.closeout))
      .catch((e) => !cancelled && setError(errorMessage(e)))
      .finally(() => !cancelled && setLoaded(true));
    return () => {
      cancelled = true;
    };
  }, [token, missionId, closable, refreshKey]);

  // Re-read while the device report is prepared; each answer schedules the next.
  useEffect(() => {
    if (!token || !missionId || !inProgress(closeout)) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchCloseout(token, missionId)
        .then((body) => !cancelled && setCloseout(body.closeout))
        .catch(() => !cancelled && setCloseout((current) => (current ? { ...current } : current)));
    }, POLL_MS);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [token, missionId, closeout]);

  const download = useCallback(async () => {
    if (!token || !missionId) return;
    setBusy(true);
    setError(null);
    try {
      await downloadCareRecord(token, missionId, incident, closeout !== null);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }, [token, missionId, incident, closeout]);

  const closeOut = async () => {
    setConfirming(false);
    if (!token || !missionId) return;
    setBusy(true);
    setError(null);
    try {
      setCloseout((await startCloseout(token, missionId)).closeout);
    } catch (e) {
      setError(errorMessage(e));
    } finally {
      setBusy(false);
    }
  };

  let label: string;
  let onClick: () => void;
  let note: React.ReactNode = null;
  if (!closable) {
    label = "Patient care record (PDF)";
    onClick = download;
  } else if (closeout === null) {
    label = "Generate ePCR";
    onClick = () => setConfirming(true);
  } else if (closeout.stage === "failed") {
    label = "Retry ePCR";
    onClick = closeOut;
    note = "Device report failed";
  } else if (closeout.stage === "ready") {
    label = "Download ePCR (PDF)";
    onClick = download;
    note = "Device report ready";
  } else {
    // Downloadable only once the device report is in it.
    label = "Generating ePCR…";
    onClick = () => undefined;
    note = `Device report being prepared since ${hhmm(closeout.requested_at)}`;
  }
  const working = busy || (closable && inProgress(closeout));
  const disabled = !missionId || busy || (closable && (!loaded || inProgress(closeout)));
  // The full reason of a failure, on hover (the note stays short).
  const failure = closeout?.stage === "failed" && closeout.error ? closeout.error : null;

  return (
    // The note hangs below the button, out of the row's layout, so it never shifts the buttons beside it.
    <Box sx={{ position: "relative" }}>
      <Button
        size="small"
        variant="contained"
        disabled={disabled}
        onClick={onClick}
        title={error ? `Failed: ${error}` : failure ? `Device report failed: ${failure}` : undefined}
        startIcon={working ? <CircularProgress size={14} sx={{ color: "inherit" }} /> : undefined}
        sx={{
          height: TOP_CONTROL_HEIGHT,
          bgcolor: ramp.neutral[950],
          color: ramp.neutral[0],
          "&:hover": { bgcolor: ramp.neutral[800] },
          "&.Mui-disabled": { bgcolor: ramp.neutral[300], color: ramp.neutral[0] },
        }}
      >
        {label}
      </Button>
      {error ? (
        <Typography variant="caption" color="error" sx={{ position: "absolute", top: "100%", right: 0, mt: 0.25, whiteSpace: "nowrap" }}>
          {error}
        </Typography>
      ) : note ? (
        <Typography variant="caption" color="text.secondary" sx={{ position: "absolute", top: "100%", right: 0, mt: 0.25, whiteSpace: "nowrap" }}>
          {note}
        </Typography>
      ) : null}

      <Dialog open={confirming} onClose={() => setConfirming(false)} maxWidth="sm">
        <DialogTitle>Generate the ePCR for {incident}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Ends the mission if it is still open (final), then builds the ePCR with the device report. Takes a few
            minutes.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirming(false)}>Cancel</Button>
          <Button variant="contained" onClick={closeOut}>
            Generate ePCR
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
